import React, { useEffect, useRef, useState } from 'react';
import './quest-map-creator.styles.scss';
import QuestMapPointPicker from './quest-map-point-picker.component';
import { properCase } from '@/utils/properCase';
import Modal, { ModalAlert } from '@/components/modal/modal.component';
import CheckpointEditForm from './checkpoint-edit-form.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import QuestMapImageNoCrop from './quest-map-image.component';
import { createPathFromTimestamp } from '@/utils/createPathFromTimestamp';

const QuestMapCreator = ({
  gameData,
  saveData,
  formData,
  gameElements
}) => {

  const [mapPoints, setMapPoints] = useState([]);
  const [checkpointToEdit, setCheckpointToEdit] = useState(null);
  const [ alertMessage, setAlertMessage ] = useState(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current && formData.checkpoints && formData.checkpoints.length > 0) {
      setMapPoints(formData.checkpoints);
      initializedRef.current = true;
    }
  }, [formData.checkpoints]);

  useEffect(() => {
    saveData('checkpoints', mapPoints);
  }, [mapPoints])

  function handlePointAdded({ xPct, yPct }) {
    setMapPoints(prev => {
      const next = [
        ...prev,
        {
          xPct,
          yPct,
          id: new Date().getTime() + '_checkpoint',
          name: 'Checkpoint ' + (prev.length + 1),
          status: 'incomplete',
          type: 'checkpoint',
          path: createPathFromTimestamp(),
          icon: '/images/icons/checkpoint.png'
        },
      ];
      return next;
    });
  }

  function removePoint(id) {
    setMapPoints(prev =>
      prev
        .filter(p => p.id !== id)
        .map((p, i) => ({ ...p, name: 'Checkpoint ' + (i + 1) }))
    );
  }

  function handlePointMoved(id, { xPct, yPct }) {
    setMapPoints(prev => prev.map(p => p.id === id ? { ...p, xPct, yPct } : p));
  }

  function saveCheckpoint(updatedCheckpoint) {
    setMapPoints(prev => prev.map(p => p.id === updatedCheckpoint.id ? updatedCheckpoint : p));
    setCheckpointToEdit(null);
  }

  return (
    <div className='quest-map-creator g-list-item form-section'>
      <h3>Quest Map and Checkpoints</h3>
      <hr />
      <div className='g-list-item map-upload-div'>
        {
          (formData.mapImageUrl)
            ?
            <QuestMapPointPicker
              gameData={gameData}
              formData={formData}
              points={mapPoints}
              onPointAdded={handlePointAdded}
              onPointMoved={handlePointMoved} />
            :
            <QuestMapImageNoCrop
              gameData={gameData}
              type='questMap'
              saveData={saveData}
              formData={formData} />
        }
      </div>
      <div className="g-space-1"></div>
      <div className="g-list-item checkpoint-list">
        <div><b>Checkpoints</b></div>
        {mapPoints.length > 0
          ?
          <table className='g-table'>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {mapPoints.map((point, i) => (
                <tr
                  key={point.id}
                  className={
                    point.status === 'incomplete'
                      ? 'row-incomplete'
                      : ''
                  }>
                  <td>{i + 1}</td>
                  <td>{point.name}</td>
                  <td>{properCase(point.status)}</td>
                  <td className='checkpoint-actions'>
                    <button
                      className='g-button small-btn'
                      onClick={() => setCheckpointToEdit(point.id)}>
                      Edit
                    </button>
                    <button
                      className='g-button small-btn'
                      onClick={() => removePoint(point.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          :
          <p className='meta'>No checkpoints added yet. Click on the map to add checkpoints.</p>
        }
      </div>
      <div className="g-space-1"></div>
      {
        checkpointToEdit !== null &&
        <Modal
          show={checkpointToEdit !== null}
          cancel={() => setCheckpointToEdit(null)}
          text='Edit Checkpoint'
          closeButton={true} >
          <div className='checkpoint-form-container'>
            <CheckpointEditForm
              checkpoint={mapPoints.find(p => p.id === checkpointToEdit)}
              saveCheckpoint={saveCheckpoint}
              gameElements={gameElements}
              gameData={gameData}
              setAlertMessage={setAlertMessage} />
          </div>
        </Modal>
      }
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
    </div>
  )
}

export default QuestMapCreator